/**
 * THE ROOT OF THE PALETTE (2026-09-01).
 *
 * Every brand colour on the platform starts here — one constant, one place.
 * `lightTheme` below derives from it, `createCssVariables` emits it as
 * `--brand-*` custom properties, and the /desktop embed's CDN token sheet is
 * generated from it (scripts/generate-desktop-embed-tokens.mjs).
 *
 * WHY IT MOVED HERE. Until this change the palette was a source of truth in
 * name only: 102 live files painted 1,237 hardcoded brand hex literals, and
 * exactly 14 read the shared variables. Editing this file changed almost
 * nothing. The literals are now `var(--brand-*)` references, so changing a
 * value here changes it everywhere — which is what a source of truth means.
 *
 * `--brand-*` values NEVER invert with a colour scheme. That is the point of
 * having them alongside the semantic `--color-*` tokens: a landing-page hero
 * that is deliberately near-black must stay near-black, while
 * `--color-text-primary` is free to flip when a dark theme is switched on.
 * Use a brand token for "this specific brand colour", a semantic token for
 * "the role this colour plays".
 *
 * Adding a colour: add it here, and `scripts/check-design-tokens.mjs` will
 * keep the literals from creeping back.
 */
export const brandColors = {
  primaryBlack: '#1A1A1A',
  darkVariant: '#111111',
  secondaryDark: '#2B2B2B',
  accentGold: '#C6A75E',
  // NOTE: one hex digit from accentGold. Both are live in the product today,
  // so both are kept as distinct tokens rather than silently unified — that
  // is a visual decision for the owner, flagged 2026-09-01, not a refactor.
  accentGoldAlt: '#C8A75E',
  hoverGold: '#B6924C',
  accentText: '#7a6125',
  backgroundSoftWhite: '#FAFAF8',
  surfaceSoft: '#F5F5F3',
  textPrimary: '#111111',
  textSecondary: '#4A4A4A',
  textPlaceholder: '#adb5bd',
  border: '#E6E6E6',
  borderStrong: '#D0D0D0',
  // The ONE alert red (2026-09-08, per-thread notification badges): the
  // unread badge on every thread surface, the account bell counter, the
  // hamburger counter and the routine error dot all painted their own red
  // literal before this token existed.
  dangerRed: '#E24A4A',
} as const;

export interface ThemeColors {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  light: string;
  dark: string;
  accent: string;
  accentHover: string;

  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textPlaceholder: string;

  background: string;
  backgroundSecondary: string;
  backgroundHover: string;
  backgroundActive: string;

  borderLight: string;
  borderMedium: string;
  borderDark: string;
  borderFocus: string;

  accentSubtle: string;
  accentText: string;
}

export interface ThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xxl: string;
}

export interface ThemeFontSizes {
  tiny: string;
  small: string;
  body: string;
  subtitle: string;
  title: string;
  heading: string;
  display: string;
}

export interface ThemeRadii {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  round: string;
  pill: string;
}

export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  focus: string;
}

export interface ThemeTransitions {
  fast: string;
  default: string;
  slow: string;
}

export interface ThemeFonts {
  display: string;
  heading: string;
  text: string;
  mono: string;
}

export interface Theme {
  colors: ThemeColors;
  spacing: ThemeSpacing;
  fontSizes: ThemeFontSizes;
  radii: ThemeRadii;
  shadows: ThemeShadows;
  transitions: ThemeTransitions;
  fonts: ThemeFonts;
}

export const lightTheme: Theme = {
  colors: {
    primary: brandColors.accentGold,
    secondary: brandColors.textSecondary,
    success: '#28a745',
    warning: '#ffc107',
    danger: '#f44336',
    info: '#17a2b8',
    light: brandColors.backgroundSoftWhite,
    dark: brandColors.primaryBlack,
    accent: brandColors.accentGoldAlt,
    accentHover: brandColors.hoverGold,

    textPrimary: brandColors.textPrimary,
    textSecondary: brandColors.textSecondary,
    textTertiary: brandColors.textSecondary,
    textPlaceholder: brandColors.textPlaceholder,

    background: brandColors.backgroundSoftWhite,
    backgroundSecondary: brandColors.surfaceSoft,
    backgroundHover: brandColors.surfaceSoft,
    backgroundActive: '#e9ecef',

    borderLight: brandColors.border,
    borderMedium: brandColors.border,
    borderDark: brandColors.borderStrong,
    borderFocus: brandColors.accentGold,

    accentSubtle: 'rgba(198, 167, 94, 0.1)',
    accentText: brandColors.accentText
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },

  fontSizes: {
    tiny: '0.75rem',
    small: '0.875rem',
    body: '1rem',
    subtitle: '1.25rem',
    title: '1.5rem',
    heading: '2rem',
    display: '3rem'
  },

  radii: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50%',
    pill: '9999px'
  },

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.05)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.05)',
    focus: '0 0 0 0.2rem rgba(198, 167, 94, 0.25)'
  },

  transitions: {
    fast: '0.15s ease',
    default: '0.3s ease',
    slow: '0.5s ease'
  },

  fonts: {
    display: "'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif",
    heading: "'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif",
    text: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'SF Mono', SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"
  }
};

export const darkTheme: Theme = {
  ...lightTheme,
  colors: {
    primary: '#C8A75E',
    secondary: '#adb5bd',
    success: '#40c057',
    warning: '#ffa94d',
    danger: '#fa5252',
    info: '#22b8cf',
    light: '#1A1A1A',
    dark: '#FAFAF8',
    accent: '#C8A75E',
    accentHover: '#B6924C',

    textPrimary: '#FAFAF8',
    textSecondary: '#E6E6E6',
    textTertiary: '#E6E6E6',
    textPlaceholder: '#adb5bd',

    background: '#111111',
    backgroundSecondary: '#1A1A1A',
    backgroundHover: '#2B2B2B',
    backgroundActive: '#4A4A4A',

    borderLight: '#2B2B2B',
    borderMedium: '#4A4A4A',
    borderDark: '#4A4A4A',
    borderFocus: '#C8A75E',

    accentSubtle: 'rgba(200, 167, 94, 0.15)',
    accentText: '#E8C87A'
  }
};

export const createCssVariables = (theme: Theme): string => {
  return `
    :root {
      /* Brand constants — never invert with a colour scheme.
         Use these for "this specific brand colour"; use the semantic
         --color-* tokens below for "the role this colour plays". */
      --brand-black: ${brandColors.primaryBlack};
      --brand-charcoal: ${brandColors.secondaryDark};
      --brand-gold: ${brandColors.accentGold};
      --brand-gold-alt: ${brandColors.accentGoldAlt};
      --brand-gold-hover: ${brandColors.hoverGold};
      --brand-gold-text: ${brandColors.accentText};
      --brand-canvas: ${brandColors.backgroundSoftWhite};
      --brand-surface: ${brandColors.surfaceSoft};
      --brand-text: ${brandColors.textPrimary};
      --brand-text-secondary: ${brandColors.textSecondary};
      --brand-text-placeholder: ${brandColors.textPlaceholder};
      --brand-line: ${brandColors.border};
      --brand-line-strong: ${brandColors.borderStrong};
      --brand-danger: ${brandColors.dangerRed};

      /* Colors */
      --color-primary: ${theme.colors.primary};
      --color-secondary: ${theme.colors.secondary};
      --color-success: ${theme.colors.success};
      --color-warning: ${theme.colors.warning};
      --color-danger: ${theme.colors.danger};
      --color-info: ${theme.colors.info};
      --color-light: ${theme.colors.light};
      --color-dark: ${theme.colors.dark};
      --color-accent: ${theme.colors.accent};
      --color-accent-hover: ${theme.colors.accentHover};

      --color-text-primary: ${theme.colors.textPrimary};
      --color-text-secondary: ${theme.colors.textSecondary};
      --color-text-tertiary: ${theme.colors.textTertiary};
      --color-text-placeholder: ${theme.colors.textPlaceholder};

      --color-background-primary: ${theme.colors.background};
      --color-background-secondary: ${theme.colors.backgroundSecondary};
      --color-background-hover: ${theme.colors.backgroundHover};
      --color-background-active: ${theme.colors.backgroundActive};

      --color-border-light: ${theme.colors.borderLight};
      --color-border-medium: ${theme.colors.borderMedium};
      --color-border-dark: ${theme.colors.borderDark};
      --color-border-focus: ${theme.colors.borderFocus};

      --color-accent-subtle: ${theme.colors.accentSubtle};
      --color-accent-text: ${theme.colors.accentText};

      /* Spacing */
      --spacing-xs: ${theme.spacing.xs};
      --spacing-sm: ${theme.spacing.sm};
      --spacing-md: ${theme.spacing.md};
      --spacing-lg: ${theme.spacing.lg};
      --spacing-xl: ${theme.spacing.xl};
      --spacing-xxl: ${theme.spacing.xxl};

      /* Font Sizes */
      --font-size-tiny: ${theme.fontSizes.tiny};
      --font-size-small: ${theme.fontSizes.small};
      --font-size-body: ${theme.fontSizes.body};
      --font-size-subtitle: ${theme.fontSizes.subtitle};
      --font-size-title: ${theme.fontSizes.title};
      --font-size-heading: ${theme.fontSizes.heading};
      --font-size-display: ${theme.fontSizes.display};

      /* Radii */
      --radius-xs: ${theme.radii.xs};
      --radius-sm: ${theme.radii.sm};
      --radius-md: ${theme.radii.md};
      --radius-lg: ${theme.radii.lg};
      --radius-xl: ${theme.radii.xl};
      --radius-round: ${theme.radii.round};
      --radius-pill: ${theme.radii.pill};

      /* Shadows */
      --shadow-sm: ${theme.shadows.sm};
      --shadow-md: ${theme.shadows.md};
      --shadow-lg: ${theme.shadows.lg};
      --shadow-xl: ${theme.shadows.xl};
      --shadow-focus: ${theme.shadows.focus};

      /* Transitions */
      --transition-fast: ${theme.transitions.fast};
      --transition-default: ${theme.transitions.default};
      --transition-slow: ${theme.transitions.slow};

      /* Fonts */
      --font-family-display: ${theme.fonts.display};
      --font-family-heading: ${theme.fonts.heading};
      --font-family-text: ${theme.fonts.text};
      --font-family-mono: ${theme.fonts.mono};
    }
  `;
};

